import type {
  CreateScalePayload,
  GetScalesPayload,
  Scale,
  ScaleListItem,
  UpdateScalePayload,
} from '../../types/index.js';
import { baseApi } from '../base-api.js';

type ScalesResponse = { scales: ScaleListItem[] };
type ScaleResponse = { scale: Scale };

const scaleListTags = (result: ScaleListItem[] | undefined) =>
  result
    ? [...result.map(({ id }) => ({ type: 'Scale' as const, id })), { type: 'Scale' as const, id: 'LIST' }]
    : [{ type: 'Scale' as const, id: 'LIST' }];

/**
 * Mirrors `apps/server/src/modules/scale` (`scaleController`, prefix `/scales`). `Question`'s
 * full shape embeds a live `scale: { id, label, code, options }` snippet (see `question.ts`), so
 * `updateScale` invalidates `Question` `LIST` too — an option/label edit would otherwise go stale
 * in any already-cached question. `deleteScale` does not: `scale.service.ts`'s
 * `deleteScaleById` throws if any question still references the scale, so a successful delete
 * can never leave a stale embed behind.
 */
export const scaleApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getScales: builder.query<ScaleListItem[], GetScalesPayload>({
      query: (payload) => ({ path: ['scales'], method: 'get', query: payload }),
      transformResponse: (response: ScalesResponse) => response.scales,
      providesTags: (result) => scaleListTags(result),
    }),

    getScaleById: builder.query<Scale, string>({
      query: (id) => ({ path: ['scales', { param: 'scale_id', value: id }], method: 'get' }),
      transformResponse: (response: ScaleResponse) => response.scale,
      providesTags: (_result, _error, id) => [{ type: 'Scale', id }],
    }),

    createScale: builder.mutation<Scale, CreateScalePayload>({
      query: (body) => ({ path: ['scales'], method: 'post', body }),
      transformResponse: (response: ScaleResponse) => response.scale,
      invalidatesTags: [{ type: 'Scale', id: 'LIST' }],
    }),

    updateScale: builder.mutation<Scale, { id: string; payload: UpdateScalePayload }>({
      query: ({ id, payload }) => ({
        path: ['scales', { param: 'scale_id', value: id }],
        method: 'put',
        body: payload,
      }),
      transformResponse: (response: ScaleResponse) => response.scale,
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Scale', id },
        { type: 'Scale', id: 'LIST' },
        { type: 'Question', id: 'LIST' },
      ],
    }),

    deleteScale: builder.mutation<void, string>({
      query: (id) => ({ path: ['scales', { param: 'scale_id', value: id }], method: 'delete' }),
      transformResponse: (): void => undefined,
      invalidatesTags: (_result, _error, id) => [
        { type: 'Scale', id },
        { type: 'Scale', id: 'LIST' },
      ],
    }),
  }),
});

export const {
  useGetScalesQuery,
  useGetScaleByIdQuery,
  useCreateScaleMutation,
  useUpdateScaleMutation,
  useDeleteScaleMutation,
} = scaleApi;
